import type { DropResult } from '@hello-pangea/dnd';
import is from '@sindresorhus/is';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useStore } from 'zustand';

import type { Competencies, JobProfileComponent, SectionTagDefinition } from '~/shared/data-contract/job-profile';
import type { JobProfileVersions } from '~/shared/data-contract/job-profile-versions';
import type { JobProfileSkill } from '~/shared/data-contract/skill';

import { useOptionalUrlTiles } from '../../url-tiles';
import type { JobItemDetails } from '../api/use-job-item-details';
import { useDraftFormSync } from '../hooks/use-draft-form-sync';
import { useHasFormChanges } from '../hooks/use-has-form-changes';
import { useProfileTaskDraftCoordinator } from '../profile-tasks/draft/ProfileTaskDraftCoordinatorProvider';
import {
    getProfileTaskParentDraftKey,
    hasProfileTaskDraftChanges,
} from '../profile-tasks/draft/profile-task-draft-coordinator';
import { JSA_PROFILE_TASK_DETAIL_TILE_TYPE } from '../profile-tasks/tile/profile-task-tile-types';
import { PROFILE_TASK_TILE_SLOT_KEY, profileTaskTileSchema } from '../profile-tasks/tile/task-tile-url';
import { useDraftVersionStore } from './DraftVersionStoreProvider';
import { useManualEditStore } from './ManualEditStoreProvider';
import { addSkillsToCompetencies, useSkillsWithCompetencies } from './skills/use-skills-with-compentencies';
import type { CompetencyWithSkills, ProfileForm } from './types';
import { useBeforeUnload } from './use-before-unload';
import { type SaveJobProfileOptions, useJobProfileFormActions } from './use-job-profile-form-actions';
import { useSkillActionStore } from './use-skill-action-store';
import { useUnsavedChangesGuard } from './use-unsaved-changes-guard';

export type UseJobProfileFormParams = {
    readonly architectureId: string;
    readonly isEditing: boolean;
    readonly item: JobItemDetails;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly onSkillProficiencyChanged?: (skillId: string, level: JobProfileSkill['level']) => void;
    readonly parentRoleId?: string;
    readonly profile: JobProfileComponent;
    readonly regeneratedSectionTags?: readonly SectionTagDefinition[];
    readonly selectedVersion: string | undefined;
    readonly setActiveVersion: (versionId: string) => void;
    readonly setIsEditing: (editing: boolean) => void;
};

export type UseJobProfileFormResult = {
    readonly allCompetencies: Competencies | undefined;
    readonly competenciesWithSkills: CompetencyWithSkills[];
    readonly formMethods: UseFormReturn<ProfileForm>;
    readonly formSkills: readonly JobProfileSkill[];
    readonly handleCancel: () => void;
    readonly handleCancelDiscard: () => void;
    readonly handleConfirmDiscard: () => void;
    readonly handleManualEdit: () => void;
    readonly handleMove: (instructions: DropResult<string>) => void;
    readonly handleNewVersion: () => void;
    readonly handleRemoveCompetency: (competencyId: string) => void;
    readonly handleSaveSubmit: (data: ProfileForm, options: SaveJobProfileOptions) => Promise<void>;
    readonly handleSaveValidationError: () => void;
    readonly hasFormChanges: boolean;
    readonly isDiscardDialogOpen: boolean;
    readonly onChangeProficiencyLevel: (skillId: string, level: JobProfileSkill['level']) => void;
    readonly removeSkill: (competencyId: string, skillId: string) => void;
    readonly versions: JobProfileVersions | undefined;
};

export const useJobProfileForm = ({
    architectureId,
    isEditing,
    item,
    itemId,
    itemType,
    onSkillProficiencyChanged,
    parentRoleId,
    profile,
    regeneratedSectionTags,
    selectedVersion,
    setActiveVersion,
    setIsEditing,
}: UseJobProfileFormParams): UseJobProfileFormResult => {
    const setHasManualEdits = useManualEditStore((state) => state.setHasManualEdits);
    const handleManualEdit = useCallback(() => {
        setHasManualEdits(true);
    }, [setHasManualEdits]);

    const draftStore = useDraftVersionStore();
    const taskCoordinator = useProfileTaskDraftCoordinator();
    const urlTiles = useOptionalUrlTiles();
    const resetSkillActionStore = useSkillActionStore((state) => state.reset);
    const hasDraft = useStore(draftStore, (state) => state.drafts.has(itemId));
    const taskContext = useMemo(
        () => ({
            architectureId,
            parentId: itemId,
            parentType: itemType === 'job-role' ? ('role' as const) : ('level' as const),
            parentVersion: Number(selectedVersion ?? item.versionInfo?.version ?? 1),
        }),
        [architectureId, item.versionInfo?.version, itemId, itemType, selectedVersion],
    );
    const taskParentKey = getProfileTaskParentDraftKey(taskContext);
    const hasTaskChanges = useStore(taskCoordinator, (state) =>
        hasProfileTaskDraftChanges(state.parents.get(taskParentKey)),
    );

    const formMethods = useForm<ProfileForm>({
        defaultValues: {
            competencies: addSkillsToCompetencies([...(profile.competencies?.values() ?? [])], profile.skills ?? []),
            skills: profile.skills ?? [],
            translations: profile.translations,
            versionNote: undefined,
        },
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration
    useEffect(() => {
        if (isEditing) {
            return;
        }
        const itemVersion = item.versionInfo?.version?.toString();
        if (selectedVersion !== undefined && itemVersion !== undefined && itemVersion !== selectedVersion) {
            return;
        }
        const nextValues: ProfileForm = {
            competencies: addSkillsToCompetencies([...(profile.competencies?.values() ?? [])], profile.skills ?? []),
            skills: profile.skills ?? [],
            translations: profile.translations,
            versionNote: undefined,
        };
        if (formMethods.formState.isDirty || !isEqual(formMethods.getValues(), nextValues)) {
            formMethods.reset(nextValues);
        }
    }, [
        isEditing,
        selectedVersion,
        item.profileGenerationInfo?.updatedAt,
        item.versionInfo?.version,
        item.profileGenerationInfo?.skillDetectionState,
        item.profileGenerationInfo?.translationState,
    ]);

    useDraftFormSync({ draftStore, formMethods, hasDraft, isEditing, itemId });

    useBeforeUnload({ shouldPreventUnload: isEditing });

    const hasProfileFormChanges = useHasFormChanges({ isEditing });
    const hasFormChanges = hasProfileFormChanges || hasTaskChanges;

    const { cancelDiscard, confirmDiscard, isDiscardDialogOpen, requestDiscard } = useUnsavedChangesGuard({
        hasUnsavedChanges: hasFormChanges,
    });

    const resetForm = useCallback(() => {
        taskCoordinator.getState().discardParent(taskContext);
        for (const tile of urlTiles?.tiles ?? []) {
            const parsedTaskTile = profileTaskTileSchema.safeParse(tile.payload);
            if (
                parsedTaskTile.success &&
                parsedTaskTile.data.type === JSA_PROFILE_TASK_DETAIL_TILE_TYPE &&
                parsedTaskTile.data.parentId === itemId &&
                parsedTaskTile.data.parentType === taskContext.parentType
            ) {
                urlTiles?.closeTile(PROFILE_TASK_TILE_SLOT_KEY);
            }
        }
        taskCoordinator.getState().clearParent(taskContext);
        setIsEditing(false);
        resetSkillActionStore();
        draftStore.getState().removeDraft(itemId);
        formMethods.reset();
    }, [draftStore, formMethods, itemId, resetSkillActionStore, setIsEditing, taskContext, taskCoordinator, urlTiles]);

    const handleCancel = useCallback(() => {
        if (hasFormChanges) {
            requestDiscard(resetForm);
            return;
        }
        resetForm();
    }, [hasFormChanges, requestDiscard, resetForm]);

    const { allCompetencies, formSkills, handleNewVersion, handleSaveSubmit, handleSaveValidationError, versions } =
        useJobProfileFormActions({
            architectureId,
            draftStore,
            formMethods,
            hasDraft,
            item,
            itemId,
            itemType,
            parentRoleId,
            profile,
            regeneratedSectionTags,
            resetSkillActionStore,
            selectedVersion,
            setActiveVersion,
            setIsEditing,
        });

    const { changeProficiencyLevel, competenciesWithSkills, move, removeCompetency, removeSkill } =
        useSkillsWithCompetencies({ formMethods, onManualEdit: handleManualEdit });

    const onChangeProficiencyLevel = useCallback(
        (skillId: string, level: JobProfileSkill['level']) => {
            changeProficiencyLevel(skillId, level);
            if (!is.nullOrUndefined(level)) {
                onSkillProficiencyChanged?.(skillId, level);
            }
        },
        [changeProficiencyLevel, onSkillProficiencyChanged],
    );

    return {
        allCompetencies,
        competenciesWithSkills,
        formMethods,
        formSkills,
        handleCancel,
        handleCancelDiscard: cancelDiscard,
        handleConfirmDiscard: confirmDiscard,
        handleManualEdit,
        handleMove: move,
        handleNewVersion,
        handleRemoveCompetency: removeCompetency,
        handleSaveSubmit,
        handleSaveValidationError,
        hasFormChanges,
        isDiscardDialogOpen,
        onChangeProficiencyLevel,
        removeSkill,
        versions,
    };
};
